import { useEffect, useRef } from 'react'
import cable from '@/lib/cable'

interface Agent {
  id: number
  name: string
  email: string
  avatar_url: string | null
  role: string
  online: boolean
}

interface EndUser {
  id: number
  name: string
  email: string | null
  company: string | null
  last_seen_at: string | null
}

interface Attachment {
  id: number
  signed_id: string
  filename: string
  content_type: string
  byte_size: number
  url: string
}

interface RepliedTo {
  id: number
  body: string | null
  author_type: 'Agent' | 'EndUser' | null
  author_name: string
  attachments_count: number
  deleted: boolean
  image_url?: string | null
}

interface Message {
  id: number
  body: string
  channel: 'chat' | 'email'
  author_type: 'Agent' | 'EndUser'
  author: Agent | EndUser
  created_at: string
  read_at: string | null
  delivered_at: string | null
  deleted_at: string | null
  edited_at: string | null
  attachments: Attachment[]
  sender_page_url: string | null
  sender_browser: string | null
  sender_os: string | null
  replied_to?: RepliedTo | null
}

interface InternalNote {
  id: number
  body: string
  agent: Agent
  created_at: string
}

export interface TypingData {
  user_id: number
  user_name: string
  user_type: string
  avatar_url: string | null
  is_typing: boolean
}

interface BroadcastData {
  type: 'message' | 'internal_note' | 'typing' | 'messages_read' | 'end_user_updated' | 'message_updated' | 'message_deleted' | 'whatsapp_send_error'
  message?: Message | MessageUpdatedData
  internal_note?: InternalNote
  typing?: TypingData
  message_ids?: number[]
  message_id?: number
  read_at?: string
  end_user?: EndUserUpdatedData
  error?: string
  error_code?: string
}

export interface MessagesReadData {
  message_ids: number[]
  read_at: string
}

export interface EndUserUpdatedData {
  id: number
  email: string | null
  name: string
  company?: string | null
  phone?: string | null
  user_actions?: Record<string, string | undefined>
  company_actions?: Record<string, string | undefined>
}

export interface MessageUpdatedData {
  id: number
  body: string
  system_message_type: 'away_notice' | 'email_capture' | null
  metadata: Record<string, unknown> | null
  edited_at: string | null
}

export interface MessageDeletedData {
  message_id: number
}

export interface WhatsAppSendErrorData {
  message_id: number
  error: string
  error_code: string
}

interface UseConversationChannelProps {
  conversationId: number
  onMessageReceived: (message: Message) => void
  onNoteReceived: (note: InternalNote) => void
  onTypingReceived?: (typingData: TypingData) => void
  onMessagesRead?: (data: MessagesReadData) => void
  onEndUserUpdated?: (data: EndUserUpdatedData) => void
  onMessageUpdated?: (data: MessageUpdatedData) => void
  onMessageDeleted?: (data: MessageDeletedData) => void
  onWhatsAppSendError?: (data: WhatsAppSendErrorData) => void
}

export function useConversationChannel({
  conversationId,
  onMessageReceived,
  onNoteReceived,
  onTypingReceived,
  onMessagesRead,
  onEndUserUpdated,
  onMessageUpdated,
  onMessageDeleted,
  onWhatsAppSendError,
}: UseConversationChannelProps) {
  const subscriptionRef = useRef<any>(null)

  // Use refs for callbacks to avoid recreating subscription
  const onMessageReceivedRef = useRef(onMessageReceived)
  const onNoteReceivedRef = useRef(onNoteReceived)
  const onTypingReceivedRef = useRef(onTypingReceived)
  const onMessagesReadRef = useRef(onMessagesRead)
  const onEndUserUpdatedRef = useRef(onEndUserUpdated)
  const onMessageUpdatedRef = useRef(onMessageUpdated)
  const onMessageDeletedRef = useRef(onMessageDeleted)
  const onWhatsAppSendErrorRef = useRef(onWhatsAppSendError)

  useEffect(() => {
    onMessageReceivedRef.current = onMessageReceived
    onNoteReceivedRef.current = onNoteReceived
    onTypingReceivedRef.current = onTypingReceived
    onMessagesReadRef.current = onMessagesRead
    onEndUserUpdatedRef.current = onEndUserUpdated
    onMessageUpdatedRef.current = onMessageUpdated
    onMessageDeletedRef.current = onMessageDeleted
    onWhatsAppSendErrorRef.current = onWhatsAppSendError
  }, [onMessageReceived, onNoteReceived, onTypingReceived, onMessagesRead, onEndUserUpdated, onMessageUpdated, onMessageDeleted, onWhatsAppSendError])

  useEffect(() => {
    // Don't subscribe if no conversation selected
    if (!conversationId) return

    // Subscribe to the conversation channel
    const subscription = cable.subscriptions.create(
      {
        channel: 'ConversationChannel',
        conversation_id: conversationId,
      },
      {
        connected() {
          // Connected to conversation channel
        },
        disconnected() {
          // Disconnected from conversation channel
        },
        received(data: BroadcastData) {
          if (data.type === 'message' && data.message) {
            onMessageReceivedRef.current(data.message as Message)
          } else if (data.type === 'internal_note' && data.internal_note) {
            onNoteReceivedRef.current(data.internal_note)
          } else if (data.type === 'typing' && data.typing && onTypingReceivedRef.current) {
            onTypingReceivedRef.current(data.typing)
          } else if (data.type === 'messages_read' && data.message_ids && data.read_at && onMessagesReadRef.current) {
            onMessagesReadRef.current({ message_ids: data.message_ids, read_at: data.read_at })
          } else if (data.type === 'end_user_updated' && data.end_user && onEndUserUpdatedRef.current) {
            onEndUserUpdatedRef.current(data.end_user)
          } else if (data.type === 'message_updated' && data.message && onMessageUpdatedRef.current) {
            onMessageUpdatedRef.current(data.message as MessageUpdatedData)
          } else if (data.type === 'message_deleted' && data.message_id && onMessageDeletedRef.current) {
            onMessageDeletedRef.current({ message_id: data.message_id })
          } else if (data.type === 'whatsapp_send_error' && data.message_id && data.error && onWhatsAppSendErrorRef.current) {
            onWhatsAppSendErrorRef.current({ message_id: data.message_id, error: data.error, error_code: data.error_code || '' })
          }
        },
      }
    )

    subscriptionRef.current = subscription

    // Cleanup: unsubscribe when component unmounts or conversationId changes
    return () => {
      subscription.unsubscribe()
      subscriptionRef.current = null
    }
  }, [conversationId])

  const notifyTyping = (isTyping: boolean) => {
    if (subscriptionRef.current) {
      subscriptionRef.current.perform('typing', { is_typing: isTyping })
    }
  }

  // Tell the server the agent has read this conversation (updates the
  // per-agent read cursor that unread counts derive from).
  const markRead = () => {
    if (subscriptionRef.current) {
      subscriptionRef.current.perform('read', {})
    }
  }

  return { notifyTyping, markRead }
}
